import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Search } from 'lucide-react';
import { PRODUCT_CATEGORIES } from '../data/mockData';
import { useProducts } from '../data/hooks';
import ProductCard from '../components/ProductCard';
import FilterSidebar, { buildEmptyFilters, MobileFilterDrawer } from '../components/FilterSidebar';
import { LoadingState, ErrorState, EmptyState } from '../components/states/AsyncStates';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';
import Img from '../components/Img';
import { EDITORIAL_HEROES } from '../data/heroImages';

const ProductDirectory = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialCat = searchParams.get('category');

  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState(() => {
    const f = buildEmptyFilters();
    if (initialCat) f.categories = [initialCat];
    return f;
  });
  const [drawer, setDrawer] = useState(false);
  const [sort, setSort] = useState('relevance');

  const { data: allProducts, loading, error, refetch } = useProducts();

  const results = useMemo(() => {
    if (!allProducts) return [];
    let arr = [...allProducts];
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q)) ||
        (p.category || '').includes(q)
      );
    }
    if (filters.categories.length) arr = arr.filter(p => filters.categories.includes(p.category));
    if (filters.countries.length) arr = arr.filter(p => (p.sourcingRegions || []).some(r => filters.countries.includes(r)));
    arr = arr.filter(p => (p.moq ?? 0) <= filters.moqMax);

    if (sort === 'moq-asc') arr.sort((a, b) => (a.moq || 0) - (b.moq || 0));
    if (sort === 'moq-desc') arr.sort((a, b) => (b.moq || 0) - (a.moq || 0));
    return arr;
  }, [allProducts, query, filters, sort]);

  // Debounced product search + filter analytics.
  useEffect(() => {
    if (!query.trim()) return;
    const id = window.setTimeout(() => {
      track(EVENTS.PRODUCT_SEARCH, { query_length: query.length, result_count: results.length });
    }, 750);
    return () => window.clearTimeout(id);
  }, [query, results.length]);
  useEffect(() => {
    const activeCount = filters.categories.length + filters.countries.length;
    if (activeCount === 0) return;
    track(EVENTS.FILTER_APPLIED, {
      surface: 'product_directory',
      active_count: activeCount,
      category_count: filters.categories.length,
      country_count: filters.countries.length,
      result_count: results.length,
    });
  }, [filters, results.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="product-directory-page" className="surface-obsidian">
      <SEO
        title="Product Directory — Fashion, Streetwear & Activewear"
        description="Browse the GIWAHS product directory — indexed SKUs across streetwear, activewear, denim, footwear, workwear and accessories. Filter by MOQ, sourcing region, and category."
        keywords="product directory, fashion sourcing, streetwear products, activewear, denim, workwear"
      />
      {/* Page header */}
      <section className="relative overflow-hidden border-b border-black" style={{ background: '#0B0B0B', color: '#F7F7F5' }}>
        <div className="absolute inset-0">
          <Img src={EDITORIAL_HEROES.products.src} alt={EDITORIAL_HEROES.products.alt} variant="hero" priority className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <span className="label-overline" style={{ color: '#C8A96B' }}>/ Product Directory</span>
          <h1 className="mt-4 font-display tracking-tight" style={{ fontWeight: 600, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 1.02 }}>
            Browse the <em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>catalogue.</em>
          </h1>
          <p className="mt-5 max-w-2xl" style={{ color: '#D9D9D9' }}>
            {allProducts?.length || 0} indexed SKUs across {PRODUCT_CATEGORIES.length} categories — filter by MOQ, sourcing region, and category to surface what fits your brief.
          </p>
        </div>
      </section>

      {/* D-080 — search & filter controls moved beneath hero image */}
      <section className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-5 md:px-8 md:py-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-platinum-2" size={14} strokeWidth={1.5} />
              <input
                data-testid="product-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products, tags, materials…"
                className="giwahs-pill-field w-full py-3 pl-11 pr-4 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setDrawer(true)} data-testid="open-filters-mobile" className="btn-giwahs btn-giwahs-outline-gold lg:hidden">
                <SlidersHorizontal size={14} strokeWidth={1.5} /> Filters
              </button>
              <select
                data-testid="product-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="btn-giwahs btn-giwahs-outline-gold giwahs-sort-select max-w-full"
              >
                <option value="relevance">Sort: Relevance</option>
                <option value="moq-asc">MOQ: Low to High</option>
                <option value="moq-desc">MOQ: High to Low</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Grid + sidebar */}
      <section className="surface-obsidian">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-5 py-10 md:px-8 lg:grid-cols-[280px_1fr]">
          <div className="hidden lg:block">
            <FilterSidebar filters={filters} setFilters={setFilters} variant="product" />
          </div>
          <div>
            <div className="mb-5 flex items-center justify-between border-b border-hairline pb-3">
              <span className="font-mono-data text-xs text-platinum-2" data-testid="product-result-count">
                {loading ? 'LOADING…' : `${results.length} RESULTS`}
              </span>
              <span className="font-mono-data text-xs text-muted-warm">FILTERED</span>
            </div>
            {loading && <LoadingState label="Loading products…" />}
            {error && !loading && <ErrorState description={error?.message || 'Failed to load products.'} onRetry={refetch} />}
            {!loading && !error && results.length === 0 && (
              <EmptyState title="No matching products" description="Try widening your filters or clearing your search." />
            )}
            {!loading && !error && results.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {results.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      <MobileFilterDrawer open={drawer} onClose={() => setDrawer(false)}>
        <FilterSidebar filters={filters} setFilters={setFilters} variant="product" />
      </MobileFilterDrawer>
    </div>
  );
};

export default ProductDirectory;
